import React, { useRef, useEffect, useState } from 'react';

// CONTEXT

// UTILS
import iconMap from 'utils/iconMap';
import { ChevronDown, Search as SearchIcon, Delete as CrossIcon } from 'lucide-react';
import TooltipComponent from './tooltipComponent';

const SearchBar = ({ searchTerm, setSearchTerm, searchEngine, setSearchEngine, handleSearch, clearSearchBar, handleSearchEngineChange, isSearchMenuOpen, setIsSearchMenuOpen }) => {
  const searchMenuRef = useRef(null);
  const inputRef = useRef(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const logos = {
    Google: iconMap['google'],
    Bing: iconMap['bing'],
    QWant : iconMap['qwant'],
    Yahoo: iconMap['yahoo'],
    DuckDuckGo: iconMap['duckduckgo'],
    Ecosia: iconMap['ecosia'],
  };

  // Icônes (remplacement par lucide-react, on ne touche pas aux logos moteurs)

  const getSearchEngineLogo = (engine) => logos[engine] || logos['Google'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchMenuRef.current && !searchMenuRef.current.contains(event.target)) {
        setIsSearchMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchMenuRef, setIsSearchMenuOpen]);

  return (
    <div ref={searchMenuRef} className={`relative transition-all duration-300 ease-in-out w-full max-w-[600px]`}>
      {/* Sélecteur de moteur de recherche */}
      <span 
        className="absolute inset-y-0 left-3 flex items-center z-10" 
        onClick={(event) => {
          event.stopPropagation();
          setIsSearchMenuOpen(!isSearchMenuOpen);
        }}
      >
        <TooltipComponent title="Changer de moteur de recherche" placement="bottom">
          <button
            type="button"
            className="relative flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-blanc-20 dark:hover:bg-blanc/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emeraude/50"
            aria-label="Sélectionner un moteur de recherche"
          >
            <img src={getSearchEngineLogo(searchEngine)} alt="Search Engine Logo" className="size-4 flex-shrink-0" />
            <ChevronDown
              className={`size-3.5 text-bleu dark:text-blanc transition-transform duration-200 ${
                isSearchMenuOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
        </TooltipComponent>

        {/* Séparateur */}
        <div className="relative right-2 flex items-center">
          <div className={`h-6 mx-3 border-l ${isInputFocused ? 'border-emeraude/50' : 'border-bleu/30 dark:border-blanc/30'} transition-colors duration-200`}></div>
        </div>

        {/* Menu déroulant des moteurs de recherche */}
        {isSearchMenuOpen && (
          <div className={`absolute left-0 top-full mt-2 w-56 bg-blanc dark:bg-noir text-noir dark:text-blanc rounded-lg shadow-xl border border-blanc-30 dark:border-blanc/30 overflow-hidden z-50 animate-fade-in`}>
            <ul className="py-1">
              {['Google', 'Bing', 'QWant', 'Yahoo', 'DuckDuckGo', 'Ecosia'].map((engine) => (
                <li key={engine}>
                  <button
                    type="button"
                    className={`w-full cursor-pointer px-4 py-2.5 flex items-center gap-3 hover:bg-bleu/10 dark:hover:bg-blanc/15 transition-colors duration-150 ${
                      searchEngine === engine ? 'bg-bleu/5 dark:bg-blanc/10' : ''
                    }`}
                    onClick={() => handleSearchEngineChange(engine)}
                  >
                    <img src={getSearchEngineLogo(engine)} alt={engine} className="size-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{engine}</span>
                    {searchEngine === engine && (
                      <span className="ml-auto text-emeraude text-xs">✓</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </span>

      {/* Input de recherche */}
      <input
        type="text"
        placeholder={`Rechercher avec ${searchEngine}...`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
        ref={inputRef}
        className={`w-full pl-20 pr-20 py-2.5 text-sm border rounded-full
          text-bleu dark:text-blanc placeholder:text-bleu/50 dark:placeholder:text-blanc/60 border-bleu/30 dark:border-blanc/40 hover:border-bleu/50 dark:hover:border-blanc/60
          bg-blanc dark:bg-noir-70
          focus:outline-none focus:ring-2 focus:ring-emeraude/30 dark:focus:ring-emeraude/50 focus:border-emeraude dark:focus:border-emeraude
          transition-all duration-200
          ${isInputFocused ? 'shadow-md shadow-emeraude/10 dark:shadow-emeraude/20' : 'shadow-sm dark:shadow-md'}`}
      />

      {/* Bouton effacer (visible quand il y a du texte) */}
      {searchTerm && (
        <>
          {/* Séparateur */}
          <div className="absolute inset-y-0 right-12 flex items-center pointer-events-none">
            <div className={`h-6 border-l border-bleu/30 dark:border-blanc/30`}></div>
          </div>
          <button
            type="button"
            className="absolute top-1/2 -translate-y-1/2 right-14 flex items-center justify-center w-8 h-8 cursor-pointer rounded-full hover:bg-blanc-20 dark:hover:bg-blanc/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emeraude/50"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { clearSearchBar(); inputRef.current && inputRef.current.focus(); }}
            aria-label="Effacer la recherche"
          >
            <CrossIcon className={`size-4 text-bleu/70 dark:text-blanc/70 hover:text-bleu dark:hover:text-blanc transition-colors`} />
          </button>
        </>
      )}

      {/* Bouton de recherche */}
      <button
        type="button"
        className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center justify-center w-8 h-8 cursor-pointer rounded-full hover:bg-emeraude/10 dark:hover:bg-emeraude/20 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emeraude/50"
        onClick={handleSearch}
        aria-label="Rechercher"
      >
        <SearchIcon className={`size-4 text-bleu dark:text-blanc hover:text-emeraude dark:hover:text-emeraude transition-colors duration-200`} />
      </button>
    </div>
  );
};

export default SearchBar;